import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  FlashOn as RapidIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ArrowForward as NextIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

// Styled components
const RulesContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: '24px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '16px',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#FF9800',
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 32px rgba(255, 152, 0, 0.15)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FF9800',
  color: '#fff',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '16px 32px',
  fontSize: '1.2rem',
  '&:hover': {
    backgroundColor: '#F57C00',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(255, 152, 0, 0.3)',
  },
}));

const RapidQuizRules = ({ session, participant, onBack, onAcknowledge }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [acknowledged, setAcknowledged] = useState(false);

  const handleAcknowledge = () => {
    setAcknowledged(true);
    enqueueSnackbar('Moving to waiting room...', { variant: 'success' });
    
    // Small delay for better UX
    setTimeout(() => {
      if (onAcknowledge) {
        onAcknowledge();
      }
    }, 1000);
  };

  const rules = [
    {
      icon: <SpeedIcon sx={{ color: '#FF9800' }} />,
      title: 'Fast-Paced Questions',
      description: 'Questions appear instantly when admin pushes them. Be ready!'
    },
    {
      icon: <TimerIcon sx={{ color: '#4CAF50' }} />,
      title: 'Quick Responses',
      description: 'Answer as fast as possible for bonus points. Speed matters!'
    },
    {
      icon: <TrophyIcon sx={{ color: '#FFD700' }} />,
      title: 'Live Leaderboard',
      description: 'See your ranking update in real-time as you play.'
    },
    {
      icon: <GroupIcon sx={{ color: '#2196F3' }} />,
      title: 'Multiplayer Competition',
      description: 'Compete against other players in real-time.'
    },
    {
      icon: <CheckIcon sx={{ color: '#4CAF50' }} />,
      title: 'Instant Feedback',
      description: 'Get immediate results after each question.'
    }
  ];

  const warnings = [
    'Questions will appear suddenly - stay alert!',
    'Answer quickly for maximum points',
    'Don\'t refresh the page during the quiz',
    'Make sure you have a stable internet connection'
  ];

  return (
    <RulesContainer>
      <AppBar position="static" sx={{ bgcolor: 'background.paper', boxShadow: 1, mb: 3 }}>
        <Toolbar>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Rapid Quiz Rules
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <RapidIcon sx={{ fontSize: 80, color: '#FF9800', mb: 2 }} />
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
            Rapid Quiz Rules
          </Typography>
          <Typography variant="h6" sx={{ color: '#ccc', fontWeight: 400 }}>
            Get ready for fast-paced, real-time quiz competition!
          </Typography>
        </Box>

        {/* Session Info */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#FF9800', mb: 2, display: 'flex', alignItems: 'center' }}>
              <RapidIcon sx={{ mr: 1 }} />
              Session: {session?.quiz_name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                  <strong>Mode:</strong> Rapid Quiz
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                  <strong>Difficulty:</strong> {session?.quiz_config?.difficulty || 'Medium'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                  <strong>Questions per Song:</strong> {session?.quiz_config?.questions_per_song || 3}
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                  <strong>Total Songs:</strong> {session?.quiz_config?.number_of_songs || 0}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        {/* Rules List */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 600 }}>
              How Rapid Quiz Works
            </Typography>
            <List>
              {rules.map((rule, index) => (
                <ListItem key={index} sx={{ py: 2 }}>
                  <ListItemIcon>
                    {rule.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                        {rule.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1" sx={{ color: '#B0B0B0', mt: 1 }}>
                        {rule.description}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </StyledCard>

        {/* Warnings */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <WarningIcon sx={{ mr: 1, color: '#FF9800' }} />
              Important Warnings
            </Typography>
            <List>
              {warnings.map((warning, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: '#FF9800', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ color: '#fff' }}>
                        {warning}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </StyledCard>

        {/* Scoring Info */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 600 }}>
              Scoring System
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#121212', borderRadius: '8px' }}>
                  <Typography variant="h6" sx={{ color: '#4CAF50', mb: 1 }}>
                    Correct Answer
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B0B0B0' }}>
                    Base points + Speed bonus
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, backgroundColor: '#121212', borderRadius: '8px' }}>
                  <Typography variant="h6" sx={{ color: '#FF9800', mb: 1 }}>
                    Speed Bonus
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B0B0B0' }}>
                    Faster answers = More points
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        {/* Action Button */}
        <Box textAlign="center">
          <ActionButton
            variant="contained"
            size="large"
            startIcon={<NextIcon />}
            onClick={handleAcknowledge}
            disabled={acknowledged}
            sx={{
              minWidth: '300px',
              py: 2,
              fontSize: '1.3rem',
              '&:disabled': {
                backgroundColor: 'rgba(255, 152, 0, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            {acknowledged ? 'Moving to Waiting Room...' : 'Next - Go to Waiting Room'}
          </ActionButton>
        </Box>

        {/* Footer */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Good luck and have fun! 🎵
          </Typography>
        </Box>
      </Container>
    </RulesContainer>
  );
};

export default RapidQuizRules;
